/**
 * Workspace Slice
 *
 * State management for workspace-related UI and extended data operations.
 * Complements authSlice which handles basic workspace list from auth.
 */

import type { WorkspaceListItem } from '../../types/workspace';

// ===== STATE INTERFACE =====

export interface WorkspaceState {
  /** Extended workspace list with member counts and descriptions */
  extendedWorkspaces: WorkspaceListItem[];
  /** Loading state for workspace list */
  isLoading: boolean;
  /** Creating workspace in progress */
  isCreating: boolean;
  /** Error message if any operation failed */
  error: string | null;
}

export interface WorkspaceUIState {
  /** Dashboard panel visibility */
  dashboard: {
    isOpen: boolean;
  };
  /** Create workspace modal state */
  createModal: {
    isOpen: boolean;
  };
  /** Edit workspace modal state */
  editModal: {
    isOpen: boolean;
    workspaceId: string | null;
  };
  /** Workspace management panel state */
  managementPanel: {
    isOpen: boolean;
    workspaceId: string | null;
    activeTab: 'members' | 'invitations' | 'settings';
  };
  /** Batch invitation modal state */
  invitationModal: {
    isOpen: boolean;
    workspaceId: string | null;
  };
}

// ===== ACTIONS INTERFACE =====

export interface WorkspaceActions {
  // Data actions
  setExtendedWorkspaces: (workspaces: WorkspaceListItem[]) => void;
  removeExtendedWorkspace: (workspaceId: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsCreating: (isCreating: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // UI actions
  openDashboard: () => void;
  closeDashboard: () => void;
  toggleDashboard: () => void;

  openCreateModal: () => void;
  closeCreateModal: () => void;

  openEditModal: (workspaceId: string) => void;
  closeEditModal: () => void;

  openManagementPanel: (
    workspaceId: string,
    tab?: 'members' | 'invitations' | 'settings'
  ) => void;
  closeManagementPanel: () => void;
  setManagementTab: (tab: 'members' | 'invitations' | 'settings') => void;

  openInvitationModal: (workspaceId: string) => void;
  closeInvitationModal: () => void;

  // Cleanup
  resetWorkspaceUI: () => void;
}

// ===== SLICE INTERFACE =====

export interface WorkspaceSlice {
  workspace: WorkspaceState & WorkspaceActions;
  workspaceUI: WorkspaceUIState;
}

// ===== INITIAL STATE =====

const initialWorkspaceState: WorkspaceState = {
  extendedWorkspaces: [],
  isLoading: false,
  isCreating: false,
  error: null,
};

const initialWorkspaceUIState: WorkspaceUIState = {
  dashboard: {
    isOpen: false,
  },
  createModal: {
    isOpen: false,
  },
  editModal: {
    isOpen: false,
    workspaceId: null,
  },
  managementPanel: {
    isOpen: false,
    workspaceId: null,
    activeTab: 'members',
  },
  invitationModal: {
    isOpen: false,
    workspaceId: null,
  },
};

// ===== SLICE CREATOR =====

export const createWorkspaceSlice = (
  set: (fn: (state: WorkspaceSlice) => void) => void,
  _get: () => WorkspaceSlice,
  _store: unknown
) => ({
  workspace: {
    ...initialWorkspaceState,

    // Data actions
    setExtendedWorkspaces: (workspaces: WorkspaceListItem[]) => {
      set((state) => {
        state.workspace.extendedWorkspaces = workspaces;
      });
    },

    removeExtendedWorkspace: (workspaceId: string) => {
      set((state) => {
        state.workspace.extendedWorkspaces =
          state.workspace.extendedWorkspaces.filter(
            (workspace) => workspace.workspaceId !== workspaceId
          );
      });
    },

    setIsLoading: (isLoading: boolean) => {
      set((state) => {
        state.workspace.isLoading = isLoading;
      });
    },

    setIsCreating: (isCreating: boolean) => {
      set((state) => {
        state.workspace.isCreating = isCreating;
      });
    },

    setError: (error: string | null) => {
      set((state) => {
        state.workspace.error = error;
      });
    },

    clearError: () => {
      set((state) => {
        state.workspace.error = null;
      });
    },

    // UI actions
    openDashboard: () => {
      set((state) => {
        state.workspaceUI.dashboard.isOpen = true;
      });
    },

    closeDashboard: () => {
      set((state) => {
        state.workspaceUI.dashboard.isOpen = false;
      });
    },

    toggleDashboard: () => {
      set((state) => {
        state.workspaceUI.dashboard.isOpen =
          !state.workspaceUI.dashboard.isOpen;
      });
    },

    openCreateModal: () => {
      set((state) => {
        state.workspaceUI.createModal.isOpen = true;
      });
    },

    closeCreateModal: () => {
      set((state) => {
        state.workspaceUI.createModal.isOpen = false;
      });
    },

    openEditModal: (workspaceId: string) => {
      set((state) => {
        state.workspaceUI.editModal.isOpen = true;
        state.workspaceUI.editModal.workspaceId = workspaceId;
      });
    },

    closeEditModal: () => {
      set((state) => {
        state.workspaceUI.editModal.isOpen = false;
        state.workspaceUI.editModal.workspaceId = null;
      });
    },

    openManagementPanel: (
      workspaceId: string,
      tab: 'members' | 'invitations' | 'settings' = 'members'
    ) => {
      set((state) => {
        state.workspaceUI.managementPanel.isOpen = true;
        state.workspaceUI.managementPanel.workspaceId = workspaceId;
        state.workspaceUI.managementPanel.activeTab = tab;
      });
    },

    closeManagementPanel: () => {
      set((state) => {
        state.workspaceUI.managementPanel.isOpen = false;
        state.workspaceUI.managementPanel.workspaceId = null;
        state.workspaceUI.managementPanel.activeTab = 'members';
      });
    },

    setManagementTab: (tab: 'members' | 'invitations' | 'settings') => {
      set((state) => {
        state.workspaceUI.managementPanel.activeTab = tab;
      });
    },

    openInvitationModal: (workspaceId: string) => {
      set((state) => {
        state.workspaceUI.invitationModal.isOpen = true;
        state.workspaceUI.invitationModal.workspaceId = workspaceId;
      });
    },

    closeInvitationModal: () => {
      set((state) => {
        state.workspaceUI.invitationModal.isOpen = false;
        state.workspaceUI.invitationModal.workspaceId = null;
      });
    },

    resetWorkspaceUI: () => {
      set((state) => {
        state.workspaceUI = { ...initialWorkspaceUIState };
      });
    },
  },

  workspaceUI: { ...initialWorkspaceUIState },
});
